// Librairies
import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule, MatRippleModule } from '@angular/material/core';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogContent, MatDialogRef } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatToolbarModule } from '@angular/material/toolbar';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

// Services
import { ModelService } from 'src/app/services/api/models/model.service';

// Interfaces
import { ExtendedModel, ModelProperty } from 'src/app/interfaces/api/model';
import { PlanningTask } from 'src/app/interfaces/api/task';
import { NameMapping, ObjectProperty, PropertyMapping } from 'src/app/interfaces/shared/mapping';

// Classes
import { Pop } from 'src/app/classes/shared/pop';

// Components
import { SearchSelectComponent } from 'projects/ui-library/src/lib/components/search-select/search-select.component';
import { ButtonComponent, SpinnerComponent } from 'ui-library';

@Component({
  selector: 'app-model-dialog',
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatDialogContent,
    MatDialogActions,
    MatDividerModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatOptionModule,
    MatRippleModule,
    MatSelectModule,
    MatToolbarModule,
    SpinnerComponent,
    ButtonComponent,
    TranslateModule,
    SearchSelectComponent
  ],
  standalone: true,
  templateUrl: './model-dialog.component.html',
  styleUrls: ['./model-dialog.component.scss']
})

export class ModelDialogComponent implements OnInit {
  // Services
  public dialogRef: MatDialogRef<ModelDialogComponent> = inject(MatDialogRef<ModelDialogComponent>);
  public readonly data: any = inject(MAT_DIALOG_DATA);
  private readonly modelService: ModelService = inject(ModelService);
  private readonly translate: TranslateService = inject(TranslateService);
  private readonly pop: Pop = inject(Pop);

  // Variables
  viewer: any;
  properties: ModelProperty[] = [];
  loading = false;
  model: ExtendedModel;
  propertyCategories: { [key: string]: string } = {
    name: 'name',
    volume: 'cote',
    length: 'cote',
    width: 'cote',
    height: 'cote',
    altitude: 'cote',
    type: 'description',
    level: 'description',
    building: 'description',
    site: 'description',
    startDate: 'planning',
    endDate: 'planning',
    duration: 'planning'
  }

  mapping: { [category: string]: PropertyMapping } = {
    element: {
      name: 'name',
      buildingType: '',
    },
    dimension: {
      volume: 'volume',
      surface: 'surface',
      thickness: 'thickness',
      weight: 'weight',
      length: 'length',
      width: 'width',
      height: 'height',
    },
    concrete: {
      concreteVolume: 'concreteVolume',
      concreteType: 'concreteType',
    },
    location: {
      level: 'level',
      building: 'building',
      site: 'site',
      altitude: 'altitude',
    },
    planning: {
      taskId: 'taskId',
      taskName: 'taskName',
      startDate: 'startDate',
      endDate: 'endDate',
      duration: 'duration'
    }
  }

  mappingArray: Array<{ category: string; names: NameMapping[] }> = [];
  buildingTypes: string[] = [];
  buildingTypeMappingArray: Array<{ name: string; value: string[] }> = [
    { name: 'wall', value: [] },
    { name: 'prefaWall', value: [] },
    { name: 'floor', value: [] },
    { name: 'beam', value: [] },
    { name: 'prefaBeam', value: [] },
    { name: 'column', value: [] },
    { name: 'prefaColumn', value: [] },
    { name: 'foundation', value: [] },
    { name: 'stair', value: [] }];

  searchValue: string = '';
  filteredProperties: ModelProperty[] = [];
  tasks: PlanningTask[] = [];
  objectCollection: { [dbId: number]: ObjectProperty } = {};
  objectsByBuildingType: { [buildingType: string]: number[] } = {};
  objectTree: any = {};
  allPropertyNames: string[] = [];

  constructor() {
    this.viewer = this.data.viewer;
    this.model = this.data.model;

    if (!this.model.buildingTypes) {

      this.model.buildingTypes = {
        wall: [],
        prefaWall: [],
        floor: [],
        beam: [],
        prefaBeam: [],
        column: [],
        prefaColumn: [],
        foundation: [],
        stair: []
      }
    } else {

      this.buildingTypeMappingArray = Object.keys(this.model.buildingTypes).map(k => {
        return {
          name: k,
          value: this.model.buildingTypes?.[k] || []
        }
      })
    }
    
    if (this.model.mapping) {

      this.mapping = { ...this.model.mapping } as { [category: string]: PropertyMapping };
    }
    this.mappingArray = Object.keys(this.mapping).map(category => {
      const categoryMapping = this.mapping[category];
      return {
        category,
        names: categoryMapping ? Object.keys(categoryMapping).map(name => {
          return { key: name, value: categoryMapping[name] || '', search: '' };
        }) : []
      }
    })
    // Object.keys(this.model.mapping).forEach(m => {

    //   this.mapping = { ...this.model.mapping };
    // });
    // if (this.mapping.element.buildingType) {
    //   this.getBuildingTypeValues();
    // }

  }

  ngOnInit(): void {
    if (this.data.modelProperties && this.data.modelProperties.length && this.data.buildingTypes) {
      this.properties = this.data.modelProperties;
      this.filteredProperties = [...this.data.modelProperties];
      this.buildingTypes = this.data.buildingTypes
      this.buildingTypes.sort();
      this.updateAllPropertyNames();
    } else {
      this.loading = true;

      this.getModelProperties().then(props => {
        this.loading = false;
        this.properties = props;
        this.filteredProperties = [...props];
        this.data.properties = this.properties;
        this.buildingTypes.sort();
        this.data.buildingTypes = this.buildingTypes;
        this.updateAllPropertyNames();
      })
    }
  }

  async getModelProperties(): Promise<ModelProperty[]> {
    try {
      var cbCount = 0;
      var modelProperties: { [key: string]: { category: string; value: string } } = {};
      var tree: any;

      const getLeafComponentsRec = async (current: any, parent: any): Promise<void> => {
        try {
          cbCount++;

          let childCount = tree.getChildCount(current);
          if (childCount != 0) {
            await new Promise(resolve => {
              tree.enumNodeChildren(current, function (children: any) {
                childCount--;
                getLeafComponentsRec(children, current).then(() => {
                  if (childCount == 0) resolve(true);
                });
              }, false);
            })
          }

          let properties = await this.getFragProperties(current, true);//expect dbId
          // let validProps = properties.filter(p => !modelProperties.includes(p))
          modelProperties = { ...modelProperties, ...properties };
        } catch (e) {
          console.error(e)
        }
      }

      tree = await this.getObjectTree();

      var rootDbId = tree.getRootId();
      // var nodeName = this.viewer.model.getInstanceTree().getNodeName(rootId)
      // fragments.push({ id: rootId, parent: '#', text: nodeName })
      await getLeafComponentsRec(rootDbId, 0);
      let properties: ModelProperty[] = [];

      Object.keys(modelProperties).forEach(k => {
        let propName = k.toLocaleLowerCase();
        let prop = properties.find(p => p.category == modelProperties[k]?.category);
        if (prop) {
          if (!prop.names.includes(propName)) {
            prop.names.push(propName);
          }
        } else {
          properties.push({
            category: modelProperties[k]?.category || null,
            names: [propName]
          })
        }
      });

      return properties;

    } catch (e) {
      console.error(e);
      return [];
    }
  }

  getObjectTree(): Promise<any> {
    return new Promise((resolve, reject) => {
      this.viewer.getObjectTree((objectTree: any) => {
        resolve(objectTree)

      });
    })
  }

  getFragProperties(dbId: number, addZone = false): Promise<{ [key: string]: { category: string; value: string } }> {
    return new Promise((resolve, reject) => {

      // let childIds = this.getLastChildren(dbId);
      // if(!handledChildren.includes())

      this.viewer.model.getProperties(dbId, (objProp: any) => {//expect dbId

        try {
          this.objectCollection[dbId] = { name: objProp.name, properties: objProp.properties };
          let props: { [key: string]: { category: string; value: string } } = {};
          objProp.properties.forEach((p: any) => {
            props[p.displayName] = { category: p.displayCategory, value: p.displayValue }

            if (p.displayName.toLowerCase() == this.mapping['element']?.['buildingType']) {
              if (!this.buildingTypes.includes(p.displayValue)) {
                this.buildingTypes.push(p.displayValue)
              }
            }
          })
          this.buildingTypes.sort()
          // let properties = objProp.properties.find(p => p.displayCategory == "Cotes" && (p.attributeName == "Volume" || p.displayName == "Volume"))
          resolve(props)
        } catch (e) {
          console.error(e)
          reject(null)
        }

      })
    });
  }

  save(): void {
    let mapping: { [category: string]: PropertyMapping } = {};
    this.mappingArray.forEach(mappingObj => {
      if (mappingObj.category) mapping[mappingObj.category] = {};
      mappingObj.names.forEach(name => {

        if (mappingObj.category) {
          if (!mapping[mappingObj.category]) {
            mapping[mappingObj.category] = {};
          }
          mapping[mappingObj.category]![name.key] = name.value;
        }

      })

    })
    this.model.mapping = mapping;
    this.model.buildingTypes = {};
    this.buildingTypeMappingArray.forEach(t => {
      if (this.model.buildingTypes) {
        this.model.buildingTypes[t.name] = t.value;
      }
    })

    this.modelService.updateModel(this.model).subscribe(res => {
      this.pop.toast(this.translate.instant('viewer.model_saved'), "success", 4000, 'save')
      this.dialogRef.close({ model: this.model, properties: this.properties, buildingTypes: this.buildingTypes });
    }, err => {

      this.pop.error(this.translate.instant('status.error'))
    })

  }


  filterInput(event: Event): void {
    const searchValue = (event.target as HTMLInputElement)?.value || '';
    if (!searchValue) return;
    const searchTerm = searchValue.toLowerCase();
    this.filteredProperties = []
    this.properties.forEach(prop => {
      let propNames = prop.names.filter(n => {
        if (typeof n === 'string') {
          return n.toLowerCase().includes(searchTerm);
        }
        return false;
      });
      if (propNames.length) {
        this.filteredProperties.push({
          category: prop.category,
          names: propNames
        })
      }
    }
    );

  }

  // onSearchFocus(): void {
  //   if (this.projectSelect && !this.projectSelect.panelOpen) {
  //     this.projectSelect.open();
  //   }
  // }

  onSelectOpen(): void {
    this.searchValue = '';
    this.filteredProperties = [...this.properties];
  }

  // getBuildingTypeValues() {
  //   new Promise((resolve, reject) => {

  //     // let childIds = this.getLastChildren(dbId);
  //     // if(!handledChildren.includes())

  //     this.viewer.model.getProperties(dbId, (objProp) => {//expect dbId

  //       try {
  //         let props = {};
  //         objProp.properties.forEach(p => props[p.displayName] = p.displayCategory)
  //         // let properties = objProp.properties.find(p => p.displayCategory == "Cotes" && (p.attributeName == "Volume" || p.displayName == "Volume"))
  //         resolve(props)
  //       } catch (e) {
  //         console.error(e)
  //         reject(null)
  //       }

  //     })
  //   });
  // }

  onMappingChanged(): void {
    if (this.mapping['element']?.['buildingType']) {
      this.getModelProperties();
    }
  }

  onCancel() {
    this.dialogRef.close({ updated: false });
  }

  isLeftColumn(category: string): boolean {
    return ['element', 'dimension', 'concrete', 'location'].includes(category);
  }

  getIconForCategory(category: string): string {
    const iconMap: { [key: string]: string } = {
      element: 'widgets',
      dimension: 'straighten',
      concrete: 'opacity',
      location: 'location_on',
      planning: 'schedule',
      buildingType: 'apartment'
    };

    return iconMap[category] || 'category';
  }

  private updateAllPropertyNames(): void {
    this.allPropertyNames = this.properties.flatMap(p =>
      p.names.filter((n): n is string => typeof n === 'string')
    );
  }
}
